











































































































import { State, Getter, Action } from 'vuex-class';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Description, Comparable_products, Manuals, Media, Placement, Questions, Related_products, Reviews, Technicals } from '@/components/sections/index';
import { Drag, Drop, DropList } from 'vue-easy-dnd';
import { Locale } from '@/enums/Locale';

@Component({
  components: {
    PagePresetDashboard: () => import('@/layouts/presets/dashboard.vue'),
    PartOverlay: () => import('@/layouts/parts/overlay.vue'),
    PartBox: () => import('@/layouts/parts/box.vue'),
    // Form elements
    FormBase: () => import('@/components/forms/Base.vue'),
    FormInput: () => import('@/components/forms/Input.vue'),
    FormWysiwyg: () => import('@/components/forms/Wysiwyg.vue'),
    FormButton: () => import('@/components/forms/Button.vue'),
    FormImageUpload: () => import('@/components/forms/ImageUpload.vue'),
    ServerError: () => import('@/components/forms/ServerError.vue'),
    LanguageSelector: () => import('@/components/parts/LanguageSelector.vue'),
    // sections
    Description,
    Comparable_products,
    Manuals,
    Media,
    Placement,
    Questions,
    Related_products,
    Reviews,
    Technicals,
    Drag,
    DropList,
    Drop,
  },
})
export default class PageProductSections extends Vue {
  @Getter('section/all') sections!: Section[];

  @Getter('workspace/selected') workspace!: Workspace;

  @Action('section/patch') patchSections!: (payload: { productId: number; sections: ProductSections; workspaceId: number }) => Promise<ProductSections>;

  @Action('section/index') indexSections!: (payload: { productId: number; workspaceId: number }) => Promise<any>;

  @Action('section-translation/index') indexSectionTranslations!: (payload: { query?: IndexQuery<SectionTranslation>; workspaceId: number }) => Promise<{ data: SectionTranslation[]; total: number }>;

  @Action('section-translation/create') createSectionTranslation!: ({ data, workspaceId }: { data: SectionTranslationData; workspaceId: number }) => Promise<SectionTranslation>;

  @Action('section-translation/update') updatSectionTranslation!: ({ id, data, workspaceId }: { id: number; data: SectionTranslationData; workspaceId: number }) => Promise<SectionTranslation>;

  i18n: string = 'views.products.show';

  $refs!: Vue['$refs'] & {
    sectionForm: FormClass;
  };

  localSections: any = [];

  formLoaded: boolean = false;

  openSections: boolean = true;

  popupAddSection: boolean = false;

  selectedLanguage = process.env.VUE_APP_DEFAULT_LOCALE;

  translationPayloads: Array<{ id?: number; payload: SectionTranslationData }> = [];

  get defaultLanguageSelected() {
    return process.env.VUE_APP_DEFAULT_LOCALE === this.selectedLanguage;
  }

  get localeDisabledClasses() {
    return { 'pointer-events-none opacity-50': !this.defaultLanguageSelected };
  }

  get submitFunction() {
    if (this.defaultLanguageSelected) {
      return this.handleSubmit;
    }
    return this.handleTranslationSubmit;
  }

  get inputItems() {
    if (this.defaultLanguageSelected) {
      return this.localSections;
    }

    return this.translationPayloads.map((ts) => ts.payload);
  }

  set inputItems(item: any) {
    if (item.payload === undefined) {
      const lsIndex = this.localSections.findIndex((ls: any) => ls.id === item.id);
      this.localSections[lsIndex] = item;
      return;
    }
    const tsIndex = this.translationPayloads.findIndex((ts) => ts.payload.section_id === item.section_id);
    this.translationPayloads[tsIndex] = { id: this.translationPayloads[tsIndex]?.id, payload: item };
  }

  async handleLanguageChange(language: Locale) {
    if (process.env.VUE_APP_DEFAULT_LOCALE === language) {
      this.selectedLanguage = language;
      return;
    }

    const freshSections = await this.indexSections({ productId: parseInt(this.$route.params.id, 10), workspaceId: this.workspace.id });
    // @ts-ignore
    this.localSections = structuredClone(freshSections);

    const sectionIds = this.localSections.filter((localSection: any) => localSection.id !== null).map((localSection: any) => localSection.id);
    const translations = await this.indexSectionTranslations({
      query: {
        q: {
          and: {
            or: {
              section_id: sectionIds,
            },
            locale: language,
          },
        },
        per_page: sectionIds.length,
      },
      workspaceId: this.workspace.id,
    });

    this.translationPayloads = this.localSections.map((localSection: any) => {
      const translation = translations.data.find((tl: SectionTranslation) => tl.section_id === localSection.id);
      return {
        id: translation?.id,
        payload: {
          ...localSection,
          ...{
            section_id: localSection.id,
            locale: language,
            content: {
              ...localSection.content,
              ...{
                title: translation?.content.title ?? localSection.content.title,
                description: translation?.content.title ?? localSection.content.description,
                description_1: translation?.content.title ?? localSection.content.description_1,
                description_2: translation?.content.title ?? localSection.content.description_2,
              },
            },
          },
        },
      };
    });

    this.selectedLanguage = language;
  }

  private handleSubmit() {
    const sections: ProductSections = this.localSections.map((section: any, index: number) => {
      return {
        ...section,
        order: index + 1,
      };
    });
    const productId = parseInt(this.$route.params.id, 10);
    this.patchSections({ productId, sections, workspaceId: this.workspace.id })
      .then(() => this.$refs.sectionForm.handleSuccess())
      .catch((e: ErrorResponse) => this.$refs.sectionForm.handleErrors(e));
  }

  private handleTranslationSubmit() {
    const allPromises: Array<Promise<any>> = [];
    this.translationPayloads.forEach((translationPayload) => {
      if (translationPayload.id === undefined) {
        allPromises.push(
          this.createSectionTranslation({ data: translationPayload.payload, workspaceId: this.workspace.id }).then((sectionTranslation) => {
            translationPayload.id = sectionTranslation.id;
          }),
        );

        return;
      }
      allPromises.push(this.updatSectionTranslation({ id: translationPayload.id, data: translationPayload.payload, workspaceId: this.workspace.id }));
    });

    Promise.all(allPromises)
      .then(() => {
        this.$refs.sectionForm.handleSuccess();
      })
      .catch((e: ErrorResponse) => this.$refs.sectionForm.handleErrors(e));
  }

  private getPayloadItem(sectionId: number) {
    if (this.defaultLanguageSelected) {
      return this.localSections.find((ls: any) => ls.id === sectionId);
    }
    return this.translationPayloads.find((tp) => tp.payload.section_id === sectionId);
  }

  private insertSection(event: any) {
    this.localSections.splice(event.index, 0, event.data);
  }

  private removeSection(sections: any, section: any) {
    const index = sections.indexOf(section);
    sections.splice(index, 1);
  }

  private handleDeleteSection(item: any) {
    item.action = 'delete';
    if (!item.id) {
      const index = this.localSections.indexOf(item);
      this.localSections.splice(index, 1);
    }
  }

  private addSection(callback: string) {
    if (!this.localSections) {
      this.localSections = [];
    }
    // @ts-ignore
    this.localSections.push(this[callback]());

    this.popupAddSection = false;
  }

  private generateEmptyDescription(): SectionDescription {
    return {
      type: 'description',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
        description: '',
      },
    };
  }
  private generateEmptyTechnicals(): SectionTechnicals {
    return {
      type: 'technicals',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
        description_1: '',
        description_2: '',
      },
    };
  }
  private generateEmptyMedia(): SectionMedia {
    return {
      type: 'media',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
        media: [],
      },
    };
  }
  private generateEmptyReviews(): SectionReviews {
    return {
      type: 'reviews',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
      },
    };
  }
  private generateEmptyQuestions(): SectionQuestions {
    return {
      type: 'questions',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
      },
    };
  }
  private generateEmptyPlacement(): SectionPlacement {
    return {
      type: 'placement',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
        media: [],
      },
    };
  }
  private generateEmptyManuals(): SectionManuals {
    return {
      type: 'manuals',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
      },
    };
  }
  private generateEmptyRelatedProduct(): SectionRelatedProducts {
    return {
      type: 'related_products',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
      },
      products: [],
    };
  }
  private generateEmptyComparableProducts(): SectionComparableProducts {
    return {
      type: 'comparable_products',
      action: 'create',
      order: this.localSections.length + 1,
      content: {
        title: '',
      },
      products: [],
    };
  }

  @Watch('sections', { immediate: true, deep: true })
  private handleProductChanged(val: ProductSections) {
    if (!val) {
      return;
    }
    const sectionsClone = JSON.parse(JSON.stringify(val));
    this.localSections = sectionsClone
      .map((section: any) => {
        return {
          action: 'update',
          ...section,
        };
      })
      .sort((a: any, b: any) => (a.order > b.order ? 1 : -1));
  }
}
